import React, { useState, useEffect, useCallback } from 'react';
import { validateEmbedConfig, sanitizeHtml } from '../utils/security';
import apiService from '../services/api';
import composioService from '../services/composio';
import {
  X,
  Copy,
  RotateCcw,
  Eye,
  Code,
  Settings,
  Info,
  Check,
  AlertCircle,
  Save,
  FolderOpen,
  Globe,
  Monitor,
  Palette,
  Languages,
  Play,
  Shield,
  Hash,
  MessageSquare,
  Plug,
  Wifi
} from 'lucide-react';

const EmbedCodeGenerator = ({ isOpen, onClose, flowId, flowName, flowNodes = [] }) => {
  const [publishing, setPublishing] = useState(false);
  const [publishedEmbed, setPublishedEmbed] = useState(null);
  const [publishError, setPublishError] = useState('');

  const [config, setConfig] = useState({
    mode: 'bubble',
    size: 'medium',
    customWidth: 420,
    customHeight: 600,
    theme: 'light',
    language: 'en',
    autoStart: false,
    position: 'bottom-right',
    title: flowName || 'AI Assistant',
    color: '#3b82f6',
    embedId: flowId || '',
    allowedDomains: '',
    trackingId: '',
    authMode: 'optional',
    composioToolkits: [],
  });

  const [generatedCode, setGeneratedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState({});
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [configName, setConfigName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Composio connections for toolkit picker
  const [connectedApps, setConnectedApps] = useState([]);
  useEffect(() => {
    composioService.getConnections().then(r => setConnectedApps(r.connections || [])).catch(() => {});
  }, []);

  function toggleToolkit(slug) {
    setConfig(prev => {
      const cur = prev.composioToolkits || [];
      return {
        ...prev,
        composioToolkits: cur.includes(slug) ? cur.filter(t => t !== slug) : [...cur, slug],
      };
    });
  }

  // Predefined sizes
  const sizes = {
    small: { width: 320, height: 480 },
    medium: { width: 420, height: 600 },
    large: { width: 520, height: 720 },
    fullscreen: { width: '100%', height: '100vh' },
    custom: { width: config.customWidth, height: config.customHeight }
  };

  // Embed modes — 3 clear customer-facing options
  const modes = [
    {
      id: 'embedded',
      name: 'Embed into Website',
      description: 'Drops the chat widget directly into a fixed area of your page as an inline fixture.',
      icon: <Globe size={16} />,
      bestFor: 'Landing pages, help centers, onboarding flows'
    },
    {
      id: 'bubble',
      name: 'Pop Up',
      description: 'A floating bubble in the bottom-right corner. On click, it opens a chat popup overlay.',
      icon: <div className="w-4 h-4 bg-blue-500 rounded-full flex-shrink-0"></div>,
      bestFor: 'Customer support, sales assistants'
    },
    {
      id: 'popup',
      name: 'Chat Window',
      description: 'A tab/bar in the corner that on click expands into a full chat window panel.',
      icon: <Monitor size={16} />,
      bestFor: 'Product showcases, guided tours, support'
    }
  ];

  // Themes
  const themes = [
    { id: 'light', name: 'Light', preview: 'bg-white text-gray-900' },
    { id: 'dark', name: 'Dark', preview: 'bg-gray-900 text-white' },
    { id: 'auto', name: 'Auto', preview: 'bg-gradient-to-r from-white to-gray-900' }
  ];

  // Languages
  const languages = [
    { id: 'en', name: 'English' },
    { id: 'es', name: 'Spanish' },
    { id: 'fr', name: 'French' },
    { id: 'de', name: 'German' },
    { id: 'it', name: 'Italian' },
    { id: 'pt', name: 'Portuguese' },
    { id: 'zh', name: 'Chinese' },
    { id: 'ja', name: 'Japanese' }
  ];

  // Positions (for bubble and popup modes)
  const positions = [
    { id: 'bottom-right', name: 'Bottom Right' },
    { id: 'bottom-left', name: 'Bottom Left' },
    { id: 'top-right', name: 'Top Right' },
    { id: 'top-left', name: 'Top Left' }
  ];

  // Auth modes
  const authModes = [
    { id: 'none', name: 'No Authentication', description: 'Public access' },
    { id: 'optional', name: 'Optional Auth', description: 'Anonymous or authenticated' },
    { id: 'required', name: 'Required Auth', description: 'Must be logged in' }
  ];

  // Validate configuration with security checks
  const validateConfig = useCallback(() => {
    // Use security validation first
    const securityValidation = validateEmbedConfig(config);
    if (!securityValidation.valid) {
      setErrors({ security: securityValidation.errors.join(', ') });
      return false;
    }

    const newErrors = {};

    if (config.size === 'custom') {
      if (!config.customWidth || config.customWidth < 200 || config.customWidth > 2000) {
        newErrors.customWidth = 'Width must be between 200-2000px';
      }
      if (!config.customHeight || config.customHeight < 300 || config.customHeight > 2000) {
        newErrors.customHeight = 'Height must be between 300-2000px';
      }
    }

    // Additional validation beyond security checks
    if (config.trackingId && config.trackingId.length > 100) {
      newErrors.trackingId = 'Tracking ID too long (max 100 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [config]);

  // Generate embed code
  const generateEmbedCode = useCallback(() => {
    if (!validateConfig()) return;

    const size = sizes[config.size];
    const baseUrl = window.location.origin;
    const embedId = config.embedId || flowId || 'replace-with-embed-config-id';
    
    let embedCode = '';

    switch (config.mode) {
      case 'bubble':
        embedCode = `<!-- AI Flow Chat Bubble -->
<script src="${baseUrl}/embed/flow-bubble.js"
        data-embed-id="${embedId}"
        data-api-base="${baseUrl}"
        data-color="${config.color}"
        data-position="${config.position}"
        data-title="${config.title}"
        data-theme="${config.theme}"
        data-language="${config.language}"
        data-auto-start="${config.autoStart}"
        data-auth="${config.authMode}"${config.allowedDomains ? `\n        data-allowed-domains="${config.allowedDomains}"` : ''}${config.trackingId ? `\n        data-tracking-id="${config.trackingId}"` : ''}>
</script>`;
        break;

      case 'popup':
        embedCode = `<!-- AI Flow Popup Modal -->
<script src="${baseUrl}/embed/flow-popup.js"
        data-embed-id="${embedId}"
        data-api-base="${baseUrl}"
        data-width="${size.width}"
        data-height="${size.height}"
        data-color="${config.color}"
        data-position="${config.position}"
        data-title="${config.title}"
        data-theme="${config.theme}"
        data-language="${config.language}"
        data-auto-start="${config.autoStart}"
        data-auth="${config.authMode}"${config.allowedDomains ? `\n        data-allowed-domains="${config.allowedDomains}"` : ''}${config.trackingId ? `\n        data-tracking-id="${config.trackingId}"` : ''}>
</script>`;
        break;

      case 'embedded':
        embedCode = `<!-- AI Flow Inline Embed -->
<div id="ai-flow-container" style="width: ${size.width}${typeof size.width === 'number' ? 'px' : ''}; height: ${size.height}${typeof size.height === 'number' ? 'px' : ''};"></div>
<script src="${baseUrl}/embed/flow-embed.js"
        data-embed-id="${embedId}"
        data-api-base="${baseUrl}"
        data-target="ai-flow-container"
        data-theme="${config.theme}"
        data-language="${config.language}"
        data-auto-start="${config.autoStart}"
        data-auth="${config.authMode}"${config.allowedDomains ? `\n        data-allowed-domains="${config.allowedDomains}"` : ''}${config.trackingId ? `\n        data-tracking-id="${config.trackingId}"` : ''}>
</script>`;
        break;
    }

    setGeneratedCode(embedCode);
  }, [config, flowId, sizes, validateConfig]);

  // Update config with sanitization
  const updateConfig = (key, value) => {
    let sanitizedValue = value;

    // Sanitize string inputs
    if (typeof value === 'string') {
      switch (key) {
        case 'title':
        case 'embedId':
        case 'allowedDomains':
        case 'trackingId':
          sanitizedValue = sanitizeHtml(value.substring(0, 200));
          break;
        case 'color':
          // Allow only valid hex colors
          if (!/^#[0-9A-Fa-f]{6}$/.test(value)) {
            sanitizedValue = '#3b82f6'; // Default blue
          }
          break;
      }
    }

    // Validate numbers
    if (typeof value === 'number') {
      if (key === 'customWidth' || key === 'customHeight') {
        sanitizedValue = Math.max(200, Math.min(2000, value));
      }
    }

    setConfig(prev => ({ ...prev, [key]: sanitizedValue }));
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Reset configuration
  const resetConfig = () => {
    setConfig({
      mode: 'bubble',
      size: 'medium',
      customWidth: 420,
      customHeight: 600,
      theme: 'light',
      language: 'en',
      autoStart: false,
      position: 'bottom-right',
      title: flowName || 'AI Assistant',
      color: '#3b82f6',
      embedId: flowId || '',
      allowedDomains: '',
      trackingId: '',
      authMode: 'optional'
    });
    setErrors({});
  };

  // Save configuration
  const saveConfiguration = () => {
    if (!configName.trim()) return;

    const newConfig = {
      id: Date.now().toString(),
      name: configName,
      config: { ...config },
      createdAt: new Date().toISOString()
    };

    const updated = [...savedConfigs, newConfig];
    setSavedConfigs(updated);
    localStorage.setItem('ai-flow-embed-configs', JSON.stringify(updated));
    setConfigName('');
    setShowSaveDialog(false);
  };

  // Load configuration
  const loadConfiguration = (savedConfig) => {
    setConfig(savedConfig.config);
    setErrors({});
  };

  // Publish embed to D1 and get real embed ID + snippet
  const publishEmbed = useCallback(async () => {
    if (!flowId || flowId === 'current-flow') {
      setPublishError('Save this flow first before publishing an embed.');
      return;
    }
    setPublishing(true);
    setPublishError('');
    try {
      const domains = config.allowedDomains
        ? config.allowedDomains.split(',').map(d => d.trim()).filter(Boolean)
        : [];
      const result = await apiService.createEmbed(flowId, {
        title: config.title,
        theme: config.theme,
        color: config.color,
        mode: config.mode,
        auth_mode: config.authMode,
        allowed_domains: domains,
        composio_toolkits: config.composioToolkits || [],
      });
      setPublishedEmbed(result);
      setGeneratedCode(result.snippet);
    } catch (err) {
      setPublishError(err.message || 'Failed to publish embed');
    } finally {
      setPublishing(false);
    }
  }, [flowId, config]);

  // Load saved configs on mount
  useEffect(() => {
    const saved = localStorage.getItem('ai-flow-embed-configs');
    if (saved) {
      try {
        setSavedConfigs(JSON.parse(saved));
      } catch (err) {
        console.error('Failed to load saved configs:', err);
      }
    }
  }, []);

  // Generate code when config changes
  useEffect(() => {
    generateEmbedCode();
  }, [generateEmbedCode]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Code size={20} className="text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Embed Code Generator</h2>
              <p className="text-sm text-gray-600">
                Generate customizable code snippets for embedding your AI Flow
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSaveDialog(true)}
              className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
              title="Save Configuration"
            >
              <Save size={16} />
            </button>
            {savedConfigs.length > 0 && (
              <div className="relative">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      const config = savedConfigs.find(c => c.id === e.target.value);
                      if (config) loadConfiguration(config);
                    }
                  }}
                  className="p-2 text-sm border border-gray-300 rounded-md bg-white"
                  defaultValue=""
                >
                  <option value="">Load Saved...</option>
                  {savedConfigs.map(config => (
                    <option key={config.id} value={config.id}>
                      {config.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Configuration Panel */}
          <div className="w-1/2 p-4 overflow-y-auto border-r border-gray-200">
            <div className="space-y-6">
              {/* Embed Mode Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <Settings size={14} className="inline mr-2" />
                  Embed Mode
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {modes.map(mode => (
                    <div
                      key={mode.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        config.mode === mode.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => updateConfig('mode', mode.id)}
                    >
                      <div className="flex items-center gap-3">
                        {mode.icon}
                        <div className="flex-1">
                          <div className="font-medium text-gray-800">{mode.name}</div>
                          <div className="text-xs text-gray-600">{mode.description}</div>
                          <div className="text-xs text-blue-600 mt-1">Best for: {mode.bestFor}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Size Configuration */}
              {config.mode !== 'bubble' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Monitor size={14} className="inline mr-2" />
                    Size
                  </label>
                  <select
                    value={config.size}
                    onChange={(e) => updateConfig('size', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="small">Small (320×480)</option>
                    <option value="medium">Medium (420×600)</option>
                    <option value="large">Large (520×720)</option>
                    <option value="fullscreen">Fullscreen</option>
                    <option value="custom">Custom</option>
                  </select>
                  
                  {config.size === 'custom' && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <input
                          type="number"
                          placeholder="Width (px)"
                          value={config.customWidth}
                          onChange={(e) => updateConfig('customWidth', parseInt(e.target.value) || 420)}
                          className={`w-full p-2 border rounded-md text-sm ${
                            errors.customWidth ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.customWidth && (
                          <div className="text-xs text-red-600 mt-1">{errors.customWidth}</div>
                        )}
                      </div>
                      <div>
                        <input
                          type="number"
                          placeholder="Height (px)"
                          value={config.customHeight}
                          onChange={(e) => updateConfig('customHeight', parseInt(e.target.value) || 600)}
                          className={`w-full p-2 border rounded-md text-sm ${
                            errors.customHeight ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.customHeight && (
                          <div className="text-xs text-red-600 mt-1">{errors.customHeight}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Position (for bubble and popup) */}
              {(config.mode === 'bubble' || config.mode === 'popup') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Position
                  </label>
                  <select
                    value={config.position}
                    onChange={(e) => updateConfig('position', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {positions.map(pos => (
                      <option key={pos.id} value={pos.id}>{pos.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Theme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Palette size={14} className="inline mr-2" />
                  Theme
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {themes.map(theme => (
                    <div
                      key={theme.id}
                      className={`p-2 border rounded-md cursor-pointer transition-all ${
                        config.theme === theme.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => updateConfig('theme', theme.id)}
                    >
                      <div className={`w-full h-8 rounded mb-1 ${theme.preview}`}></div>
                      <div className="text-xs text-center">{theme.name}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={config.title}
                  onChange={(e) => updateConfig('title', e.target.value)}
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="AI Assistant"
                />
                {errors.title && (
                  <div className="text-xs text-red-600 mt-1">{errors.title}</div>
                )}
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Accent Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={config.color}
                    onChange={(e) => updateConfig('color', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.color}
                    onChange={(e) => updateConfig('color', e.target.value)}
                    className={`flex-1 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm ${
                      errors.color ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="#3b82f6"
                  />
                </div>
                {errors.color && (
                  <div className="text-xs text-red-600 mt-1">{errors.color}</div>
                )}
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Languages size={14} className="inline mr-2" />
                  Language
                </label>
                <select
                  value={config.language}
                  onChange={(e) => updateConfig('language', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {languages.map(lang => (
                    <option key={lang.id} value={lang.id}>{lang.name}</option>
                  ))}
                </select>
              </div>

              {/* Auto Start */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.autoStart}
                    onChange={(e) => updateConfig('autoStart', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <Play size={14} />
                  <span className="text-sm font-medium text-gray-700">Auto-start flow</span>
                  <div className="group relative">
                    <Info size={12} className="text-gray-400 cursor-help" />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      Automatically begin flow when widget loads
                    </div>
                  </div>
                </label>
              </div>

              {/* Authentication */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Shield size={14} className="inline mr-2" />
                  Authentication
                </label>
                <select
                  value={config.authMode}
                  onChange={(e) => updateConfig('authMode', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {authModes.map(auth => (
                    <option key={auth.id} value={auth.id}>
                      {auth.name} - {auth.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Allowed Domains */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Hash size={14} className="inline mr-2" />
                  Embed ID
                </label>
                <input
                  type="text"
                  value={config.embedId}
                  onChange={(e) => updateConfig('embedId', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="dev_embed or your D1 embed_configs.id"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Must match an `embed_configs.id` row in the Cloudflare D1 database.
                </div>
              </div>

              {/* Allowed Domains */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe size={14} className="inline mr-2" />
                  Allowed Domains (Optional)
                </label>
                <input
                  type="text"
                  value={config.allowedDomains}
                  onChange={(e) => updateConfig('allowedDomains', e.target.value)}
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.allowedDomains ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="example.com, subdomain.example.com"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Comma-separated list of domains where this embed can be used
                </div>
                {!config.allowedDomains?.trim() && (
                  <div style={{ marginTop: '0.35rem', padding: '0.35rem 0.6rem', background: 'color-mix(in srgb, #f59e0b 12%, transparent)', border: '1px solid color-mix(in srgb, #f59e0b 40%, transparent)', borderRadius: 4, fontSize: '0.7rem', color: '#92400e' }}>
                    ⚠ Without allowed domains, the chatbot will only work on flow.7h3.agency. Add your website domain to use it externally.
                  </div>
                )}
                {errors.allowedDomains && (
                  <div className="text-xs text-red-600 mt-1">{errors.allowedDomains}</div>
                )}
              </div>

              {/* Tracking ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Hash size={14} className="inline mr-2" />
                  Tracking ID (Optional)
                </label>
                <input
                  type="text"
                  value={config.trackingId}
                  onChange={(e) => updateConfig('trackingId', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="analytics-id-123"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Unique identifier for analytics and tracking
                </div>
              </div>

              {/* Composio App Tools */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Plug size={14} className="inline mr-2" />
                  Agent App Tools (Optional)
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Enable connected apps so the embedded AI can take actions (send emails, create issues, etc.) using your account.
                </p>
                {connectedApps.length === 0 ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    No apps connected — go to Settings → Integrations to connect apps first.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {connectedApps.map(app => {
                      const active = (config.composioToolkits || []).includes(app.toolkit);
                      return (
                        <button
                          key={app.toolkit}
                          type="button"
                          onClick={() => toggleToolkit(app.toolkit)}
                          className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                            active
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {active && <Check size={11} />}
                          {app.toolkit}
                        </button>
                      );
                    })}
                  </div>
                )}
                {(config.composioToolkits || []).length > 0 && (
                  <p className="text-xs text-blue-600 mt-1.5">
                    <Wifi size={11} className="inline mr-1" />
                    {config.composioToolkits.length} app{config.composioToolkits.length !== 1 ? 's' : ''} enabled for this embed
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Preview and Code Panel */}
          <div className="w-1/2 flex flex-col">
            {/* Preview Area */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Eye size={16} className="text-gray-600" />
                <h3 className="font-medium text-gray-800">Live Preview</h3>
              </div>
              
              <div className="bg-gray-100 rounded-lg p-4 min-h-32 relative">
                {config.mode === 'bubble' && (
                  <div className="absolute bottom-4 right-4">
                    <div 
                      className="w-12 h-12 rounded-full shadow-lg flex items-center justify-center cursor-pointer"
                      style={{ backgroundColor: config.color }}
                    >
                      <MessageSquare size={20} className="text-white" />
                    </div>
                  </div>
                )}
                
                {config.mode === 'popup' && (
                  <div className="absolute bottom-4 right-4">
                    <div 
                      className="px-3 py-2 rounded-lg shadow-lg text-white text-sm cursor-pointer"
                      style={{ backgroundColor: config.color }}
                    >
                      {config.title}
                    </div>
                  </div>
                )}
                
                {config.mode === 'embedded' && (
                  <div 
                    className={`w-full border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center ${
                      config.theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
                    }`}
                    style={{ 
                      height: config.size === 'custom' ? `${Math.min(config.customHeight, 200)}px` : '200px',
                      borderColor: config.color 
                    }}
                  >
                    <div className="text-center">
                      <div className="font-medium">{config.title}</div>
                      <div className="text-sm opacity-75">Embedded Flow</div>
                    </div>
                  </div>
                )}
                
                <div className="absolute top-2 left-2 text-xs text-gray-500">
                  {config.mode} mode • {config.theme} theme
                </div>
              </div>
            </div>

            {/* Generated Code */}
            <div className="flex-1 p-4 flex flex-col gap-3">
              {/* Publish to Cloud */}
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex-1">
                  {publishedEmbed ? (
                    <div className="text-xs text-green-700 font-medium">
                      ✓ Embed live · ID: <span className="font-mono">{publishedEmbed.id}</span>
                    </div>
                  ) : (
                    <div className="text-xs text-blue-700">
                      Publish to Cloudflare D1 to get a live embed ID + chatbot snippet
                    </div>
                  )}
                  {publishError && <div className="text-xs text-red-600 mt-0.5">{publishError}</div>}
                </div>
                <button
                  onClick={publishEmbed}
                  disabled={publishing}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 whitespace-nowrap"
                >
                  {publishing ? 'Publishing...' : (publishedEmbed ? '↺ Re-publish' : '⬆ Publish & Activate')}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Code size={16} className="text-gray-600" />
                  <h3 className="font-medium text-gray-800 text-sm">
                    {publishedEmbed ? 'Live Embed Snippet' : 'Preview Snippet'}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={resetConfig}
                    className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:text-gray-800 transition-colors text-xs"
                  >
                    <RotateCcw size={12} />
                    Reset
                  </button>
                  <button
                    onClick={copyToClipboard}
                    disabled={!generatedCode}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md transition-colors text-sm disabled:opacity-40 ${
                      copied ? 'bg-green-100 text-green-700' : 'bg-gray-800 text-white hover:bg-gray-900'
                    }`}
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="flex-1 relative">
                <textarea
                  value={generatedCode}
                  readOnly
                  className="w-full h-full p-3 border border-gray-300 rounded-md font-mono text-xs bg-gray-50 resize-none focus:outline-none"
                  placeholder="Configure settings above then click Publish & Activate to get your live embed snippet..."
                />
                {Object.keys(errors).length > 0 && (
                  <div className="absolute top-2 right-2 bg-red-100 border border-red-200 rounded-md p-2">
                    <div className="flex items-center gap-1 text-red-700 text-xs">
                      <AlertCircle size={12} />
                      Fix errors first
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Configuration Dialog */}
      {showSaveDialog && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-4 w-80">
            <h3 className="font-medium text-gray-800 mb-3">Save Configuration</h3>
            <input
              type="text"
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
              placeholder="Configuration name..."
              className="w-full p-2 border border-gray-300 rounded-md mb-3"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="px-3 py-1.5 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveConfiguration}
                disabled={!configName.trim()}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbedCodeGenerator;
